<template>
  <!-- Header — dropped on a `meta.bare` page (the welcome letter, MYAC-1522): the
       app is one click away there, and its chrome would turn the letter into a page. -->
  <HomeHeader v-if="!bare" />

  <!-- Banners -->
  <AppBanners v-if="!bare" />

  <!-- Notification preferences setup dialog -->
  <NotificationSetupDialog v-if="showNotificationSetup && !bare" />

  <!-- Cross-tenant navigation prompt -->
  <CrossTenantPromptDialog />

  <div id="home-layout" class="flex-1 flex flex-nowrap items-stretch min-w-0">
    <!-- MYAC-1393 — overflow-x-auto, not hidden: a view too wide for a phone
         scrolls inside the content area (the header stays put) instead of
         being cut off where nobody can reach it. -->
    <main class="flex-1 min-w-0 overflow-x-auto flex flex-col">
      <!-- fullPath keying remounts the routed view on ANY query change — the
           historical default here. Routes that manage their own query params
           (tabs, filters) opt out with meta.ownsQuery and only remount when the
           PATH changes (project switch included, since :pid is in the path). -->
      <!-- MYAC-1522 — sibling addresses of ONE page (the Pilotage sections) share
           a `meta.viewKey`: moving between them keeps the page, a project switch
           still remounts it (the pid is in the key). A project shell (menu +
           score) is keyed per project and keys its own inner page the same way
           (usePageViewKey), so the menu never remounts between a project's pages. -->
      <router-view :key="viewKey" />
    </main>
    <!-- MYAC-917 — global chat panel: lives OUTSIDE the keyed router-view, so the
         surface (and the conversation) survives every navigation. In docked mode
         it is a flex sibling of <main> and pushes content; in overlay mode the
         Drawer teleports and this spot renders nothing. -->
    <GlobalChatPanel v-if="!bare" />
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { storeToRefs } from 'pinia'
import { useRoute } from 'vue-router'
import HomeHeader from '@/components/shared/HomeHeader.vue'
import AppBanners from '@/components/shared/AppBanners.vue'
import GlobalChatPanel from '@/components/chat/GlobalChatPanel.vue'
import NotificationSetupDialog from '@/components/shared/NotificationSetupDialog.vue'
import { shouldShowNotificationSetup } from '@/utils/notificationSetup'
import CrossTenantPromptDialog from '@/components/shared/CrossTenantPromptDialog.vue'
import { useUsersStore } from '@/stores/users'
import { useProjectsStore } from '@/stores/projects'
import { useSavedPromptsSubscription } from '@/composables/useSavedPromptsSubscription'
import { useChatActivityWatch } from '@/composables/useChatActivity'
import { useShellViewKey } from '@/composables/usePageViewKey'

const viewKey = useShellViewKey()
const usersStore = useUsersStore()
const projectsStore = useProjectsStore()

// MYAC-1140 — the app's ONE owner of the saved-prompts listener. It belongs to
// the shell, not to a page: the chat dock is a sibling of the router-view and
// outlives it, so the picker has to keep its prompts across every navigation.
useSavedPromptsSubscription()

// Redesign (MYAC-1522, block 3) — the assistant's live sign: working / ready and
// unseen, read by the dock's edge tab and the project menu from any screen.
useChatActivityWatch()

const { isNotificationPreferencesSetup } = storeToRefs(usersStore)

const route = useRoute()
const bare = computed(() => route.meta.bare === true)

const showNotificationSetup = computed(() => shouldShowNotificationSetup({
  setupDone: isNotificationPreferencesSetup.value,
  projectCount: projectsStore.tenantProjects.length,
  projectsLoaded: projectsStore.loadedOnce,
  skippedThisSession: Boolean(sessionStorage.getItem('mac.skipNotifSetupThisSession')),
  onboardingStepsMissing: usersStore.missingOnboardingSteps.length,
  assessmentProjectCount: projectsStore.tenantProjects.filter(p => p.type === 'qms' || p.type === 'td').length,
  watchReportAsked: usersStore.notificationPreferences?.watch_report_asked ?? false,
}))
</script>
